import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    FiClock, FiCheckCircle, FiXCircle, FiPlay,
    FiAlertCircle, FiTrash2, FiRefreshCcw, FiSearch,
    FiList, FiCheckSquare, FiActivity
} from 'react-icons/fi';

import { useUpload } from '../../context/UploadContext';

const TasksAdmin = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { tasks, removeTask, clearTasks, fetchHistory } = useUpload(); // Use global tasks
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');

    const isArabic = i18n.language?.startsWith('ar');

    const fetchTasks = async () => {
        setLoading(true);
        await fetchHistory();
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('admin.common.confirm_delete', "Are you sure?"))) return;
        await removeTask(id);
    };

    const handleClearAll = async () => {
        if (!window.confirm(t('admin.tasks.confirm_clear', "Clear all task history?"))) return;
        await clearTasks();
    };

    const handleRetry = (task) => {
        let payload = task.payload;
        if (typeof payload === 'string') {
            try { payload = JSON.parse(payload); } catch (e) { }
        }

        if (task.task_type === 'CREATE') {
            navigate('/admin/create-product', { state: { retryData: payload } });
        } else {
            const editId = task.task_id || payload.id;
            navigate(`/admin/edit-product/${editId}`, { state: { retryData: payload } });
        }
    };

    const formatDate = (val) => {
        if (!val) return '—';
        try {
            // Some browsers fail on "YYYY-MM-DD HH:MM:SS" (MySQL format)
            // Replacing space with T makes it ISO compliant
            const normalized = typeof val === 'string' ? val.replace(' ', 'T') : val;
            const d = new Date(normalized);
            if (isNaN(d.getTime())) return '—';
            return d.toLocaleString();
        } catch (e) {
            return '—';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success': return <FiCheckCircle className="text-green-500" />;
            case 'error': return <FiXCircle className="text-red-500" />;
            case 'running': return <FiPlay className="text-blue-500 animate-pulse" />;
            case 'interrupted': return <FiAlertCircle className="text-yellow-500" />;
            default: return <FiClock className="text-gray-400" />;
        }
    };

    const filterOptions = [
        { id: 'all', label: t('admin.tasks.filter_all', 'All'), icon: <FiList /> },
        { id: 'running', label: t('admin.tasks.filter_running', 'Running'), icon: <FiActivity /> },
        { id: 'success', label: t('admin.tasks.filter_success', 'Success'), icon: <FiCheckSquare /> },
        { id: 'error', label: t('admin.tasks.filter_error', 'Error'), icon: <FiXCircle /> },
        { id: 'interrupted', label: t('admin.tasks.filter_interrupted', 'Interrupted'), icon: <FiAlertCircle /> }
    ];

    const filteredTasks = tasks.filter(task => {
        const matchesFilter = filter === 'all' || task.status === filter;
        const matchesSearch = task.name?.toLowerCase().includes(search.toLowerCase()) ||
            task.id.toLowerCase().includes(search.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    return (
        <div className="p-6 max-w-7xl mx-auto" dir={isArabic ? 'rtl' : 'ltr'}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 tracking-tight">{t('admin.tasks.title', 'Background Tasks')}</h1>
                    <p className="text-gray-500 text-sm font-medium">{t('admin.tasks.subtitle', 'Monitor and manage long-running background uploads')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchTasks}
                        className="p-3 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all text-gray-500"
                        title={t('admin.common.refresh', 'Refresh')}
                    >
                        <FiRefreshCcw className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button
                        onClick={handleClearAll}
                        className="px-6 py-3 bg-red-50 text-red-600 rounded-2xl hover:bg-red-100 transition-all flex items-center gap-2 text-xs font-black uppercase tracking-widest shadow-sm"
                    >
                        <FiTrash2 /> {t('admin.tasks.clear_history', 'Clear History')}
                    </button>
                    <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex flex-col xl:flex-row gap-6">
                    <div className="relative flex-1">
                        <FiSearch className={`absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`} />
                        <input
                            type="text"
                            placeholder={t('admin.common.search_placeholder', 'Search...')}
                            className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 bg-gray-50 border-none rounded-2xl focus:ring-4 focus:ring-brand-primary/10 transition-all placeholder-gray-400 text-sm font-medium`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {filterOptions.map(opt => (
                            <button
                                key={opt.id}
                                onClick={() => setFilter(opt.id)}
                                className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${filter === opt.id
                                    ? 'bg-brand-primary text-white shadow-lg shadow-brand-primary/20'
                                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100/50'
                                    }`}
                            >
                                {opt.icon}
                                <span>{opt.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-50/50 text-gray-400 text-[10px] font-black uppercase tracking-[0.15em]">
                                <th className={`px-6 py-5 ${isArabic ? 'text-right' : 'text-left'}`}>{t('admin.tasks.header_task', 'Task')}</th>
                                <th className={`px-6 py-5 ${isArabic ? 'text-right' : 'text-left'}`}>{t('admin.tasks.header_status', 'Status')}</th>
                                <th className={`px-6 py-5 ${isArabic ? 'text-right' : 'text-left'}`}>{t('admin.tasks.header_progress', 'Progress')}</th>
                                <th className={`px-6 py-5 ${isArabic ? 'text-right' : 'text-left'}`}>{t('admin.tasks.header_date', 'Started At')}</th>
                                <th className={`px-6 py-5 ${isArabic ? 'text-left' : 'text-right'}`}>{t('admin.tasks.header_actions', 'Actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {filteredTasks.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <FiActivity size={40} className="text-gray-100" />
                                            <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">
                                                {t('admin.tasks.no_tasks', 'No tasks found')}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredTasks.map(task => (
                                    <tr key={task.id} className="hover:bg-gray-50/30 transition-colors group">
                                        <td className="px-6 py-5">
                                            <div className="font-black text-gray-900">{task.name}</div>
                                            <div className="text-[10px] text-gray-400 font-bold font-mono tracking-tighter mt-0.5">{task.id}</div>
                                            <div className="inline-flex mt-2 px-2 py-0.5 bg-brand-primary/5 rounded-lg text-[9px] font-black text-brand-primary uppercase tracking-widest">
                                                {task.task_type}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center">
                                                    {getStatusIcon(task.status)}
                                                </div>
                                                <span className="text-sm font-bold capitalize text-gray-700">{task.status}</span>
                                            </div>
                                            <div className="text-[10px] text-gray-400 font-medium mt-1.5 truncate max-w-[200px]">
                                                {task.message}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="w-36">
                                                <div className="flex justify-between items-center mb-1.5">
                                                    <span className="text-xs font-black text-gray-900">{task.progress}%</span>
                                                </div>
                                                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden shadow-inner p-0.5">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-700 relative overflow-hidden ${task.status === 'success' ? 'bg-green-500' :
                                                            task.status === 'error' ? 'bg-red-500' :
                                                                task.status === 'interrupted' ? 'bg-yellow-500' :
                                                                    'bg-brand-primary'
                                                            }`}
                                                        style={{ width: `${task.progress}%` }}
                                                    >
                                                        {task.status === 'running' && (
                                                            <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">
                                            {formatDate(task.created_at || task.timestamp)}
                                        </td>
                                        <td className="px-6 py-5 text-right">
                                            <div className={`flex items-center gap-1 ${isArabic ? 'justify-start' : 'justify-end'}`}>
                                                {(task.status === 'error' || task.status === 'interrupted') && (
                                                    <button
                                                        onClick={() => handleRetry(task)}
                                                        className="p-2.5 text-blue-500 hover:bg-blue-50 rounded-xl transition-all"
                                                        title={t('admin.tasks.retry', 'Retry')}
                                                    >
                                                        <FiRefreshCcw size={18} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(task.id)}
                                                    className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                    title={t('admin.common.delete', 'Delete')}
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};

export default TasksAdmin;
